import { ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";
import { useNewsletterConfig } from "../hooks/useNewsletterConfig";
import { useNavigate } from "react-router-dom";
import { useNLSearch } from "../hooks/useNLSearch";
import { NewsletterContext } from "../context/NewsletterContext";
import { useContext } from "react";

export const Footer: React.FC<{
  currentStep: number;
  setCurrentStep: (step: number) => void;
  canProceed: boolean;
}> = ({ currentStep, setCurrentStep, canProceed }) => {
  const { saveNewsletterConfig } = useNewsletterConfig();
  const navigate = useNavigate();
  const { config, preview } = useContext(NewsletterContext);
  const { searchForArticles } = useNLSearch();

  const getDaysAgo = (frequency: string) => {
    switch (frequency) {
      case "daily":
        return 1;
      case "weekly":
        return 7;
      case "biweekly":
        return 14;
      case "monthly":
        return 30;
      default:
        return 7; // fallback
    }
  };

  const handleNext = async () => {
    if (!canProceed) return;
    try {
      if (
        currentStep === 1 &&
        (preview?.articles.length === 0 || preview === null)
      ) {
        const articles = await searchForArticles({
          query: config.topics.join(", "),
          searchMode: "summary",
          daysAgo: getDaysAgo(config.frequency || "weekly"),
          MATCH_COUNT: 7,
        });

        // Always save config (with or without articles) to ensure embedding gets cached
        const newPreview = {
          subject: "",
          body: "",
          articles: articles, // This will be empty array if no articles found
        };
        await saveNewsletterConfig(newPreview);
      } else if (currentStep === 2 && preview?.body) {
        // Save the newsletter preview when moving from step 2 to step 3
        await saveNewsletterConfig(preview);
      }
      setCurrentStep(Math.min(4, currentStep + 1));
    } catch (error) {
      console.error("Failed to save newsletter config:", error);
    }
  };

  return (
    <div className="flex justify-between w-full pt-6 pb-2">
      {currentStep === 1 ? (
        <button
          onClick={() => navigate("/dashboard/newsletter/setup")}
          className="border border-black bg-white text-black hover:bg-gray-50 hover:-translate-y-1 transition-all rounded-lg px-5 py-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4 inline" />
          Back to Summary
        </button>
      ) : (
        <button
          onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
          className="border border-black bg-white text-black hover:bg-gray-50 hover:-translate-y-1 transition-all rounded-lg px-5 py-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4 inline" />
          Previous
        </button>
      )}

      {currentStep === 4 ? (
        <button
          onClick={() => {
            navigate("/dashboard/newsletter");
          }}
          className="bg-black text-white hover:bg-gray-800 hover:-rotate-1 hover:transform transition-all rounded-lg px-5 py-2"
        >
          <CheckCircle className="mr-2 h-4 w-4 inline" />
          Finish Setup
        </button>
      ) : (
        <button
          onClick={() => handleNext()}
          disabled={!canProceed}
          className={`${
            canProceed
              ? "bg-black text-white hover:bg-gray-800 hover:-rotate-1 hover:transform"
              : "bg-gray-300 cursor-not-allowed"
          } transition-all rounded-lg px-5 py-2`}
        >
          Next
          <ArrowRight className="ml-2 h-4 w-4 inline" />
        </button>
      )}
    </div>
  );
};

export const Header: React.FC<{
  currentStep: number;
}> = ({ currentStep }) => {
  const STEPS = [
    { step: 1, title: "Set your topics", color: "#e0f2fe" },
    { step: 2, title: "Set your vibe", color: "#fce7f3" },
    { step: 3, title: "Set your schedule", color: "#e0f2fe" },
    { step: 4, title: "Test your setup", color: "#dcfce7" },
  ];

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Progress Section */}
      <div
        className="relative mb-4"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={(currentStep / 4) * 100}
      >
        <div className="flex mb-3 text-sm font-semibold text-gray-800">
          <span className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-indigo-600"></div>
            Step {currentStep} of 4
          </span>
        </div>
        <div className="w-full h-3 bg-gray-200 rounded-sm border border-gray-200 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500 ease-in-out"
            style={{ width: `${(currentStep / 4) * 100}%` }}
          />
        </div>
      </div>

      {/* Step titles */}
      <div className="mb-8 flex justify-between">
        {STEPS.map((step) => (
          <div
            key={step.step}
            className={`text-center transition-all duration-300 ${
              step.step === currentStep
                ? "font-medium transform scale-110"
                : "text-gray-500 text-sm"
            }`}
            style={{
              width: "25%",
              opacity: step.step < currentStep ? 0.7 : 1,
            }}
          >
            {step.title}
          </div>
        ))}
      </div>
    </div>
  );
};
